if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.contentLoaded = false;
        this.prefetchedMediaIds = new Set();
      }

      hide() {
        super.hide();
      }

      show(opener) {
        this.loadDeferredContent();
        const mediaId = opener?.getAttribute('data-media-id');
        this.prefetchMedia(mediaId, 0, true);
        super.show(opener);
        this.showActiveMedia();
      }

      loadDeferredContent() {
        if (this.contentLoaded) return;

        const template = this.querySelector('template[data-product-modal-content]');
        const container = this.querySelector('[role="document"]');
        if (!template || !container) {
          this.contentLoaded = true;
          return;
        }

        const content = template.content.cloneNode(true);
        content.querySelectorAll('img[data-media-id]').forEach((img) => {
          const mediaId = img.getAttribute('data-media-id');
          if (!this.prefetchedMediaIds.has(mediaId)) return;
          img.loading = 'eager';
          img.removeAttribute('loading');
        });

        container.appendChild(content);
        template.remove();
        this.contentLoaded = true;
      }

      getContentRoot() {
        if (this.contentLoaded) {
          return this.querySelector('[role="document"]');
        }

        const template = this.querySelector('template[data-product-modal-content]');
        return template ? template.content : null;
      }

      getModalImages() {
        const root = this.getContentRoot();
        if (!root) return [];
        return Array.from(root.querySelectorAll('img[data-media-id]'));
      }

      prefetchMedia(mediaId, radius = 0, highPriority = false) {
        if (!mediaId) return;

        const images = this.getModalImages();
        if (!images.length) return;

        const index = images.findIndex(
          (img) => img.getAttribute('data-media-id') === String(mediaId)
        );
        if (index === -1) return;

        for (let offset = -radius; offset <= radius; offset++) {
          const image = images[index + offset];
          if (!image) continue;
          this.warmModalImage(image, highPriority && offset === 0);
        }
      }

      warmModalImage(img, highPriority = false) {
        if (!img) return;

        const mediaId = img.getAttribute('data-media-id');
        if (mediaId && this.prefetchedMediaIds.has(mediaId) && !highPriority) return;

        if (img.getAttribute('loading') === 'lazy') {
          img.loading = 'eager';
          img.removeAttribute('loading');
        }

        img.fetchPriority = highPriority ? 'high' : 'low';

        const srcset = img.getAttribute('srcset');
        const sizes = img.getAttribute('sizes') || '1100px';
        const src = img.currentSrc || img.src;

        if (srcset || src) {
          const probe = new Image();
          probe.fetchPriority = highPriority ? 'high' : 'low';
          if (sizes) probe.sizes = sizes;
          if (srcset) probe.srcset = srcset;
          probe.src = src;
          if (mediaId) {
            const markPrefetched = () => this.prefetchedMediaIds.add(mediaId);
            if (probe.complete) {
              markPrefetched();
            } else {
              probe.addEventListener('load', markPrefetched, { once: true });
              probe.addEventListener('error', markPrefetched, { once: true });
            }
          }
        } else if (mediaId) {
          this.prefetchedMediaIds.add(mediaId);
        }
      }

      showActiveMedia() {
        const mediaId = this.openedBy.getAttribute('data-media-id');
        this.querySelectorAll(`[data-media-id]:not([data-media-id="${mediaId}"])`).forEach(
          (element) => {
            element.classList.remove('active');
          }
        );

        const activeMedia = this.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');

        if (activeMedia.tagName === 'IMG') {
          activeMedia.loading = 'eager';
          activeMedia.fetchPriority = 'high';
        }

        const container = this.querySelector('[role="document"]');
        this.centerActiveMedia(activeMedia, container);

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }

      centerActiveMedia(activeMedia, container) {
        if (!activeMedia || !container) return;

        const position = () => {
          const mediaWidth = activeMedia.offsetWidth || activeMedia.width || 0;
          const mediaHeight = activeMedia.offsetHeight || activeMedia.height || 0;
          if (!mediaWidth || !mediaHeight) return;
          container.scrollLeft = Math.max(0, (mediaWidth - container.clientWidth) / 2);
          container.scrollTop = Math.max(0, (mediaHeight - container.clientHeight) / 2);
        };

        position();
        requestAnimationFrame(position);

        if (activeMedia.tagName === 'IMG' && !activeMedia.complete) {
          activeMedia.addEventListener('load', position, { once: true });
        }
      }
    }
  );
}
